/**
 * ApprovalDrawer — single-row detail view + approve / deny actions.
 *
 * Lives behind the `<Sheet>` rendered by `ApprovalsBrowser`. Renders:
 *   - tool name + kind
 *   - JSON args the model proposed (read-only, syntax-friendly mono)
 *   - lineage (agent, run, requested-by, expires-at, required role)
 *   - the resolution itself if already decided
 *   - approve / deny + a 256-char note when the row is still pending
 *     and the viewer's role satisfies `required_role`
 *
 * Conflicts (409) and forbidden (403) are toasted with action-specific
 * copy — generic "request failed" hides the bit the operator needs to
 * decide what to do next ("retry", "ask Alice", "give up").
 */

import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { ApprovalDecision, ApprovalResolveRequest, ApprovalSummary } from "@/lib/types";

interface ApprovalDrawerProps {
  approval: ApprovalSummary;
  agentName: string;
  viewerRole: string;
  viewerUserId: string;
  onResolved: (updated: ApprovalSummary) => void;
  onClose: () => void;
}

/**
 * Render a "who" line for a requester or decider.
 *
 * Falls back gracefully across three cases:
 *   1. The viewer is the actor → "you" (no badge).
 *   2. The gateway populated `displayName` (and optionally `via`) →
 *      show the friendly name plus a small uppercase pill that names
 *      the channel (e.g. "VIA FEISHU"). The full `feishu:ou_xxx` label
 *      lives in the title attribute for hover/inspector inspection.
 *   3. No display_name (deleted user, internal caller) → fall back to
 *      the same hex prefix the v1 UI used everywhere.
 */
function ActorLabel({
  userId,
  displayName,
  via,
  viewerUserId,
}: {
  userId: string;
  displayName: string | null;
  via: string | null;
  viewerUserId: string;
}) {
  if (userId === viewerUserId) {
    return <span>you</span>;
  }
  const label = displayName ?? userId.slice(0, 8);
  const channelKind = via?.split(":")[0] ?? null;
  return (
    <span className="inline-flex items-center gap-1">
      <span>{label}</span>
      {channelKind ? (
        <span
          className="rounded bg-muted/50 px-1 py-px text-[9px] uppercase tracking-wider text-muted-foreground"
          title={via ?? undefined}
        >
          via {channelKind}
        </span>
      ) : null}
    </span>
  );
}

export function ApprovalDrawer({
  approval,
  agentName,
  viewerRole,
  viewerUserId,
  onResolved,
  onClose,
}: ApprovalDrawerProps) {
  const t = useT();
  const [note, setNote] = useState<string>("");
  const [busy, setBusy] = useState<ApprovalDecision | null>(null);

  // Match the backend's role check (gateway L1a uses simple set
  // membership — no hierarchy yet). This is purely a UX gate; the API
  // re-verifies on every resolve.
  const canResolve = approval.status === "pending" && viewerRole === approval.required_role;

  const argsJson = useMemo(() => {
    try {
      return JSON.stringify(approval.tool_arguments, null, 2);
    } catch {
      return "{}";
    }
  }, [approval.tool_arguments]);

  async function resolve(decision: ApprovalDecision): Promise<void> {
    if (busy !== null) return;
    setBusy(decision);
    try {
      const body: ApprovalResolveRequest = { decision };
      const trimmed = note.trim();
      if (trimmed.length > 0) body.note = trimmed.slice(0, 256);
      const updated = await api<ApprovalSummary>(`/approvals/${approval.id}/resolve`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      toast.success(
        t(decision === "approve" ? "approvals.toast.approved" : "approvals.toast.denied"),
      );
      onResolved(updated);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // The error string from `api()` is "{status} {body}". Sniff it
      // for the specific cases we have action-friendly copy for.
      if (message.startsWith("409")) {
        toast.warning(t("approvals.toast.conflict"));
        // Conflict means somebody else decided — close so the parent
        // re-fetch reflects the new state.
        onClose();
      } else if (message.startsWith("403")) {
        toast.error(t("approvals.toast.forbidden", { role: approval.required_role }));
      } else {
        toast.error(message);
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <SheetHeader className="border-b border-border px-5 py-4">
        <SheetTitle className="flex items-center gap-2 text-base">
          <span className="font-mono text-sm">{approval.tool_name}</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {approval.tool_kind}
          </span>
        </SheetTitle>
        <p className="text-xs text-muted-foreground">
          {t("approvals.drawer.tool")} · {agentName}
        </p>
      </SheetHeader>

      <div className="flex-1 space-y-4 overflow-y-auto px-5 pb-4">
        <Field label={t("approvals.drawer.toolCallId")}>
          <code className="block break-all rounded bg-muted/40 px-2 py-1 text-[11px] font-mono text-muted-foreground">
            {approval.tool_call_id}
          </code>
        </Field>

        <Field label={t("approvals.drawer.arguments")}>
          <pre className="max-h-[280px] overflow-auto rounded-lg border border-border bg-muted/20 p-3 text-[11px] leading-snug font-mono">
            {argsJson}
          </pre>
        </Field>

        {approval.reason ? (
          <Field label={t("approvals.drawer.reason")}>
            <p className="text-xs text-muted-foreground whitespace-pre-wrap">{approval.reason}</p>
          </Field>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("approvals.drawer.required.role")}>
            <span className="font-mono text-xs">{approval.required_role}</span>
          </Field>
          <Field label={t("approvals.drawer.requestedBy")}>
            <span className="text-[11px] text-muted-foreground">
              <ActorLabel
                userId={approval.requested_by_user_id}
                displayName={approval.requested_by_display_name}
                via={approval.requested_via}
                viewerUserId={viewerUserId}
              />
            </span>
          </Field>
        </div>

        {approval.status !== "pending" ? (
          <Field label={t("approvals.drawer.decided")}>
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold uppercase tracking-wider">
                {t(`approvals.status.${approval.status}`)}
              </span>
              {approval.decided_by_user_id ? (
                <>
                  {" "}
                  ·{" "}
                  <ActorLabel
                    userId={approval.decided_by_user_id}
                    displayName={approval.decided_by_display_name}
                    via={approval.decided_via}
                    viewerUserId={viewerUserId}
                  />
                </>
              ) : null}
              {approval.decided_at ? (
                <> · {new Date(approval.decided_at).toLocaleString()}</>
              ) : null}
            </p>
            {approval.note ? (
              <p className="mt-1 rounded bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
                {approval.note}
              </p>
            ) : null}
          </Field>
        ) : null}

        <div className="flex items-center gap-2 text-xs">
          <Link
            to="/evaluations"
            className="text-primary hover:underline"
            // The activity page lives at /evaluations and renders runs
            // by request_id when query is present. We pre-link the
            // request id; the page is responsible for landing on the
            // right session.
          >
            {t("approvals.action.viewRun")} →
          </Link>
          <span className="text-muted-foreground">request {approval.request_id.slice(0, 8)}</span>
        </div>

        {canResolve ? (
          <Field label={t("approvals.note.label")}>
            <Textarea
              value={note}
              maxLength={256}
              rows={3}
              placeholder={t("approvals.note.placeholder")}
              onChange={(e) => setNote(e.target.value)}
              className="text-xs"
            />
            <p className="mt-1 text-right text-[10px] text-muted-foreground">{note.length} / 256</p>
          </Field>
        ) : approval.status === "pending" ? (
          <p className="rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-600 dark:text-amber-400">
            {t("approvals.drawer.notice.cantResolve", { role: approval.required_role })}
          </p>
        ) : null}
      </div>

      {canResolve ? (
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void resolve("deny");
            }}
            disabled={busy !== null}
          >
            {busy === "deny" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            {t("approvals.action.deny")}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              void resolve("approve");
            }}
            disabled={busy !== null}
          >
            {busy === "approve" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            {t("approvals.action.approve")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}
